import { Box, Button, Flex, FormControl, FormLabel, Input, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { ChangeEventHandler, FormEventHandler, useEffect, useState } from 'react';
import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { unitAssessmentState } from '../../UnitAssessmentState';
import { useSnapshot } from 'valtio';
import { trpcAdmin } from '../../../lib/trpcAdmin';
import { DukeTiptapEditor } from '@duke-hq/ui/components/DukeTiptapEditor/DukeTiptapEditor';
import { useDukeFileUpload } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/blocknode/useDukeFileUpload';
import { JSONContent } from '@tiptap/core';
import { JSONContentSchema } from '@duke-hq/shared-constants/tiptap';
import { Flag } from '@duke-hq/flags/react';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { LinkedRequirementsSection } from '../LinkedRequirementsSection';

export function OpenQuestionEditor({ refetch }: { refetch: () => void }) {
  const unitAssessmentSnap = useSnapshot(unitAssessmentState);
  const [formData, setFormData] = useState({
    title: '',
  });
  const [initialQuestionContent, setInitialQuestionContent] = useState<JSONContent | undefined>(
    undefined,
  );
  const [initialHintContent, setInitialHintContent] = useState<JSONContent | undefined>(undefined);
  const [initialBenchmarkContent, setInitialBenchmarkContent] = useState<JSONContent | undefined>(
    undefined,
  );
  const [editorQuestionContent, setEditorQuestionContent] = useState<JSONContent | undefined>(
    undefined,
  );
  const [editorHintContent, setEditorHintContent] = useState<JSONContent | undefined>(undefined);
  const [editorBenchmarkContent, setEditorBenchmarkContent] = useState<JSONContent | undefined>(
    undefined,
  );

  const { uploadFile } = useDukeFileUpload({
    fileAttributes: {
      attachedToKind: 'UNIT_ASSESSMENT',
      attachedToId: unitAssessmentSnap.currentAssessmentId,
    },
  });

  useEffect(() => {
    const slide = unitAssessmentState.currentSlide;
    setFormData({
      title: slide?.title || '',
    });
    setInitialQuestionContent(
      (slide && 'questionContent' in slide ? slide.questionContent : undefined) || {
        type: 'doc',
        content: [],
      },
    );
    setInitialHintContent(
      (slide && 'hint' in slide ? slide.hint : undefined) || { type: 'doc', content: [] },
    );
    const parsedBenchmark =
      slide && 'benchmarkAnswer' in slide && slide.benchmarkAnswer !== null
        ? JSONContentSchema.safeParse(slide.benchmarkAnswer)
        : null;
    setInitialBenchmarkContent(
      parsedBenchmark?.success ? parsedBenchmark.data : { type: 'doc', content: [] },
    );
  }, [unitAssessmentSnap.currentSlide, unitAssessmentSnap.isEditorOpen]);

  const createOpenQuestionSlide = trpcAdmin.unitAssessment.createOpenQuestionSlide.useMutation({
    onSuccess: () => {
      unitAssessmentState.closeEditor();
      unitAssessmentState.setHasUnpublishedChanges(true);
      refetch();
    },
  });
  const updateOpenQuestion = trpcAdmin.unitAssessment.updateOpenQuestion.useMutation({
    onSuccess: () => {
      unitAssessmentState.closeEditor();
      unitAssessmentState.setHasUnpublishedChanges(true);
      refetch();
    },
  });

  const onChangeQuestionEditor = (content: JSONContent) => {
    setEditorQuestionContent(content);
  };
  const onChangeHintEditor = (content: JSONContent) => {
    setEditorHintContent(content);
  };
  const onChangeBenchmarkEditor = (content: JSONContent) => {
    setEditorBenchmarkContent(content);
  };
  const onFormChange: ChangeEventHandler<any> = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  const onFormSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (unitAssessmentSnap.editorMode === EditorMode.CREATE) {
      await createOpenQuestionSlide.mutateAsync({
        assessmentId: unitAssessmentSnap.currentAssessmentId,
        title: formData.title,
        questionContent: editorQuestionContent ?? initialQuestionContent,
        hint: editorHintContent ?? initialHintContent,
        benchmarkAnswer: editorBenchmarkContent ?? initialBenchmarkContent,
        unitAssessmentVersionId: unitAssessmentSnap.draftVersionId,
      });
      return;
    }
    if (unitAssessmentSnap.editorMode === EditorMode.EDIT && unitAssessmentSnap.currentSlide) {
      await updateOpenQuestion.mutateAsync({
        id: unitAssessmentSnap.currentSlide.id,
        title: formData.title,
        questionContent: editorQuestionContent ?? initialQuestionContent,
        hint: editorHintContent ?? initialHintContent,
        benchmarkAnswer: editorBenchmarkContent ?? initialBenchmarkContent,
        position: unitAssessmentSnap.currentSlide.position,
      });
      return;
    }
  };

  return (
    <form onSubmit={onFormSubmit} style={{ width: '100%' }}>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel>
            <Trans>Slide Title</Trans>
          </FormLabel>
          <Input
            autoFocus
            type="text"
            autoComplete="Off"
            placeholder="Title"
            name="title"
            value={formData.title}
            onChange={onFormChange}
          />
        </FormControl>

        <LinkedRequirementsSection />

        <Box w="full" h="full">
          <FormControl isRequired>
            <FormLabel>
              <Trans>Question</Trans>
            </FormLabel>
            <Box
              w="100%"
              borderWidth="1px"
              borderRadius="md"
              outline="1px"
              boxShadow="sm"
              minH="450px"
            >
              <DukeTiptapEditor
                initialContent={initialQuestionContent}
                onChange={onChangeQuestionEditor}
                uploadFn={uploadFile}
              />
            </Box>
          </FormControl>
        </Box>

        <Box w="full" h="full">
          <FormControl>
            <FormLabel>
              <Trans>Hint</Trans>
            </FormLabel>
            <Box
              w="100%"
              borderWidth="1px"
              borderRadius="md"
              outline="1px"
              boxShadow="sm"
              minH="250px"
            >
              <DukeTiptapEditor
                initialContent={initialHintContent}
                onChange={onChangeHintEditor}
                uploadFn={uploadFile}
              />
            </Box>
          </FormControl>
        </Box>

        <Flag.Boolean
          flagKey="BENCHMARK_ANSWER_RELEASE"
          whenTrue={
            <Box w="full" h="full">
              <FormControl>
                <FormLabel>
                  <Trans>Benchmark answer</Trans>
                </FormLabel>
                <Box
                  w="100%"
                  borderWidth="1px"
                  borderRadius="md"
                  boxShadow="sm"
                  minH={pxToRem(250)}
                >
                  <DukeTiptapEditor
                    initialContent={initialBenchmarkContent}
                    onChange={onChangeBenchmarkEditor}
                    uploadFn={uploadFile}
                  />
                </Box>
              </FormControl>
            </Box>
          }
          whenFalse={<span />}
        />

        <Flex justify={'flex-end'}>
          <Button right={0} type="submit">
            {unitAssessmentSnap.editorMode === EditorMode.CREATE ? (
              <Trans>Add</Trans>
            ) : (
              <Trans>Update</Trans>
            )}
          </Button>
        </Flex>
      </VStack>
    </form>
  );
}
